import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Input, Button, Tag, Space, Tooltip, Select, App,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
    SearchOutlined, EyeOutlined, WarningOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';
import dayjs from 'dayjs';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function AdminUsers() {
    const [users, setUsers]           = useState([]);
    const [loading, setLoading]       = useState(true);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [search, setSearch]         = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [flaggedOnly, setFlaggedOnly]   = useState(false);
    const [perPage, setPerPage]           = useState(10);

    const navigate = useNavigate();
    const { notification } = App.useApp();

    const fetchUsers = useCallback(async (page = 1, searchVal = search, size = perPage) => {
        setLoading(true);
        try {
            const params = { page, per_page: size || 10 };
            if (searchVal)              params.search      = searchVal;
            if (filterActive !== 'all') params.is_deleted  = filterActive === 'active' ? 0 : 1;
            if (flaggedOnly)            params.flagged_only = 1;

            const res = await adminApi.get('/admin/users', { params });
            const d = res.data.data;
            setUsers(d.data);
            setPagination({ current: d.current_page, pageSize: d.per_page, total: d.total });
        } catch (_) {
            notification.error({ message: 'Failed to Load', description: 'Could not fetch the user list. Please try again.' });
        } finally {
            setLoading(false);
        }
    }, [search, filterActive, flaggedOnly, perPage, notification]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchUsers(1); }, [filterActive, flaggedOnly]);

    const handleSearch = (val) => {
        setSearch(val);
        fetchUsers(1, val);
    };

    const handlePerPageChange = (val) => {
        setPerPage(val);
        fetchUsers(1, search, val);
    };


    const columns = [
        {
            title: 'User',
            render: (_, row) => {
                const primary = row.profiles?.find((p) => p.is_primary) || row.profiles?.[0];
                return (
                    <div>
                        <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                            {row.first_name} {row.last_name}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{row.email}</div>
                        {primary?.profile_id && (
                            <div style={{ fontSize: 11, color: 'var(--color-primary)', fontWeight: 600, marginTop: 2 }}>
                                {primary.profile_id}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Gender',
            dataIndex: 'gender',
            width: 90,
            render: (v) => <Tag color={v === 'Male' ? 'blue' : v === 'Female' ? 'pink' : 'default'}>{v}</Tag>,
        },
        {
            title: 'Status',
            width: 170,
            render: (_, row) => (
                <Space size={4} wrap>
                    {!row.is_deleted ? <Tag color="green">Active</Tag> : <Tag color="orange">Inactive</Tag>}
                    {row.is_flagged && (
                        <Tooltip title={row.flag_reason || 'No reason given'}>
                            <Tag color="volcano">Flagged</Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Joined',
            dataIndex: 'created_at',
            width: 110,
            render: (v) => dayjs(v).format('D MMM YYYY'),
        },
        {
            title: 'Last Login',
            dataIndex: 'last_logged_in',
            width: 110,
            render: (v) => v
                ? dayjs(v).format('D MMM YYYY')
                : <span style={{ color: 'var(--color-text-muted)' }}>Never</span>,
        },
        {
            title: 'Actions',
            width: 60,
            render: (_, row) => (
                <Tooltip title="View Detail">
                    <Button
                        type="text" size="small" icon={<EyeOutlined />}
                        onClick={() => navigate(`/admin/users/${row.id}`)}
                        style={{ color: 'var(--color-primary)' }}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <AdminLayout title="Users">
            <div className="admin-page-header">
                <h1 className="admin-page-title">User Management</h1>
                <p className="admin-page-subtitle">View, manage and moderate all registered users</p>
            </div>

            <div className="admin-card">
                <div className="admin-card-header">
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: 1 }}>
                        {/* Plain Input — avoids the double-icon / border-radius issue of Input.Search */}
                        <Input
                            placeholder="Search by name, email, phone or profile ID..."
                            allowClear
                            prefix={<SearchOutlined style={{ color: 'var(--color-text-muted)' }} />}
                            value={search}
                            onChange={(e) => { if (!e.target.value) handleSearch(''); else setSearch(e.target.value); }}
                            onPressEnter={(e) => handleSearch(e.target.value)}
                            style={{ width: '100%', maxWidth: 320 }}
                        />
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={() => handleSearch(search)}
                            style={{ background: 'var(--gradient-primary)', border: 'none' }}
                        >
                            Search
                        </Button>
                        <Select
                            value={filterActive}
                            onChange={(v) => setFilterActive(v)}
                            style={{ width: 130 }}
                            options={[
                                { value: 'all',      label: 'All Users' },
                                { value: 'active',   label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                            ]}
                        />
                        <Button
                            type={flaggedOnly ? 'primary' : 'default'}
                            danger={flaggedOnly}
                            icon={<WarningOutlined />}
                            onClick={() => setFlaggedOnly((p) => !p)}
                        >
                            Flagged Only
                        </Button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Per page:</span>
                        <Select
                            value={perPage}
                            onChange={handlePerPageChange}
                            style={{ width: 80 }}
                            options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: n }))}
                        />
                        <Button icon={<ReloadOutlined />} onClick={() => fetchUsers(pagination.current)}>
                            Refresh
                        </Button>
                    </div>
                </div>

                <Table
                    className="admin-table"
                    dataSource={users}
                    columns={columns}
                    rowKey="id"
                    loading={{ spinning: loading, indicator: <LoadingOutlined style={{ fontSize: 32, color: '#AD1457' }} spin /> }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        showQuickJumper: false,
                        showTotal: (total) => `${total} users`,
                        onChange: (page) => fetchUsers(page),
                    }}
                    scroll={{ x: 800 }}
                    size="middle"
                />
            </div>

        </AdminLayout>
    );
}
